import { useRouter } from '@application/utils';
import bannerBackgroundImage from '@assets/banner-background-image.svg';
import bannerImage from '@assets/suggestion-banner-image.webp';
import {
  Banner,
  Button,
  CardItemBreeder,
  Container,
  EmptyOverlay,
  LoadingContainer,
  MtLayouTitleEditButton,
  MtLayoutTitleWrapper,
  Section,
  SectionCardItemSlider,
  SectionSubtitle,
  SectionTitle,
} from '@presentation/components';
import { Illustration } from '@presentation/components/Illustration';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { TbArrowNarrowLeft, TbArrowNarrowRight, TbListSearch } from 'react-icons/tb';
import { Card as ApplicationCard } from './components/Card';
import type { CardData } from './components/Card/types';
import * as Styled from './styled';
import type { MtApplicationsPageProps } from './types';

export * from './types';

const MAX_SLIDER_ITEMS_TO_SHOW = 3;

const SLIDER_SETTINGS = {
  dots: true,
  slidesToShow: MAX_SLIDER_ITEMS_TO_SHOW,
  slidesToScroll: MAX_SLIDER_ITEMS_TO_SHOW,
  responsive: [
    {
      breakpoint: BREAKPOINTS.desktop,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: BREAKPOINTS.small,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

export const MtApplicationsPage: FC<MtApplicationsPageProps> = (props) => {
  const {
    title,
    topCards,
    cards,
    columnTitles,
    loading = false,
    personType,
    emptyTitle,
    emptyText,
    showArchived,
    breederItems,
    onToggleShowArchived,
    onOpenSuggestionsModal,
  } = props;

  const { translate } = useTranslation();
  const { navigateToRoute } = useRouter();

  const cols = (
    <Styled.HeaderWrapper>
      {columnTitles.map((columnTitle, index) => (
        <div key={index}>{columnTitle}</div>
      ))}
      <div />
    </Styled.HeaderWrapper>
  );

  const renderCards = (items: CardData[]) =>
    items.map((item, index) => (
      <Styled.CardWrapper key={index}>
        <ApplicationCard {...item} />
      </Styled.CardWrapper>
    ));

  const isEmpty = !loading && !cards.length && !topCards?.length;

  return (
    <Container>
      <MtLayoutTitleWrapper>
        <SectionTitle>{title}</SectionTitle>
        {onToggleShowArchived && (
          <MtLayouTitleEditButton onClick={() => onToggleShowArchived(!showArchived)} color="dark">
            {showArchived ? (
              <>
                <TbArrowNarrowLeft />
                <span>{translate('common.buttons.back')}</span>
              </>
            ) : (
              <>
                <span>{translate('glossary.archives')}</span>
                <TbArrowNarrowRight />
              </>
            )}
          </MtLayouTitleEditButton>
        )}
      </MtLayoutTitleWrapper>

      {loading && <LoadingContainer />}

      {isEmpty && (
        <Section>
          <EmptyOverlay icon={<Illustration type="paws" />} title={emptyTitle} text={emptyText}>
            {personType === 'buyer' && (
              <Button
                size="medium"
                color="blue"
                iconLeft={<TbListSearch />}
                shrink
                onClick={() => navigateToRoute('litterList')}
              >
                {translate('common.buttons.explore')}
              </Button>
            )}
          </EmptyOverlay>
        </Section>
      )}

      {!loading && !!topCards?.length && (
        <Section>
          <SectionSubtitle>
            {translate('glossary.notSeen')}
            <Styled.CountWrapper>{topCards?.length}</Styled.CountWrapper>
          </SectionSubtitle>
          {cols}
          {renderCards(topCards)}
        </Section>
      )}

      {!loading && !!cards.length && (
        <Section style={topCards?.length ? { marginTop: 50 } : {}}>
          {!!topCards?.length && <SectionSubtitle>{translate('glossary.seen')}</SectionSubtitle>}
          {cols}
          {renderCards(cards)}
        </Section>
      )}

      {!isEmpty && personType === 'buyer' && !loading && onOpenSuggestionsModal && (
        <Styled.SuggestionWrapper>
          <Banner
            title={translate('common.banners.suggestions.title')}
            text={translate('common.banners.suggestions.text')}
            btnText={translate('common.buttons.discover')}
            theme="blue"
            backgroundImage={bannerBackgroundImage}
            image={bannerImage}
            onClick={onOpenSuggestionsModal}
          />
        </Styled.SuggestionWrapper>
      )}

      {breederItems && breederItems.length > 0 && (
        <SectionCardItemSlider
          title={translate('mt.applications.suggestedBreeders')}
          items={breederItems.map((item, index) => (
            <CardItemBreeder style={{ height: 375 }} key={index} {...item} />
          ))}
          overrideSettings={{
            ...SLIDER_SETTINGS,
            infinite: breederItems?.length > MAX_SLIDER_ITEMS_TO_SHOW,
          }}
          centered={false}
        />
      )}
    </Container>
  );
};
